import RadioboxElm from 'atoms/RadioboxElm';
import ButtonCta from 'atoms/ButtonCta';
import PlanDisplay from 'aa/vue/components/account/pages/accountSwitchPlan/variants/parts/PlanDisplay';
import { mapState, mapGetters, mapMutations } from 'vuex';
import NotificationBarModel from 'aa/vue/models/NotificationBarModel';
import AccountService from 'aa/vue/services/AccountService';
import { logType, pageType } from 'aa/services/AccountPageTrackingService';
import { getLocale } from 'services/LocalizationService';
import { AA_XHR_BASE_PATH } from 'aa/vue/constants/aaRoutes';
import Product from 'aa/vue/models/Product';
import { FTC_COMPLIANCE_ENABLED } from 'helpers/featureConstants';
import { MAX_DISCOUNT_ALREADY_APPLIED } from 'aa/vue/constants/shared';
import { COUPON_TYPES } from 'aa/vue/models/CouponDiscount';

export default {
    components: {
        ButtonCta,
        RadioboxElm,
        PlanDisplay,
    },
    data() {
        return {
            isProcessing: false,
            canSwitchPlan: true,
            selectedID: null,
            isValidPromoList: {},
        };
    },
    computed: {
        ...mapState('AccountSwitchPlan', {
            userProduct: (state) => state.userProduct,
            promo: (state) => state.promo,
            authToken: (state) => state.authToken,
        }),

        ...mapGetters('plan', ['getSelectedPlan', 'getCurrentPlan']),
        ...mapGetters('ftc', ['getCoolingRightsWaived']),

        cancelConfig() {
            return {
                styleClass: ['button', 'secondary'],
                displayText: this.content.get(
                    'ap_switch_plan_negative_cta',
                    this.$getLocale('cancel'),
                ),
                event: this.cancelOnClicked,
                disabled: this.isProcessing,
            };
        },
        submitConfig() {
            return {
                styleClass: ['button', 'primary'],
                displayText: this.content.get(
                    'ap_switch_plan_positive_cta',
                    this.$getLocale('switch_plan'),
                ),
                event: this.submitOnClicked,
                disabled: this.isProcessing || !this.canSwitchPlan,
            };
        },
        ftcEnabled() {
            return !!this.$store.getters.featureIsActive(FTC_COMPLIANCE_ENABLED);
        },
    },
    watch: {
        products(value) {
            this.applyDefaultSelectedProduct(value);
        },
        selectedID(value) {
            let product = this.products.filter((product) => {
                if (this.userProduct.plan_type === product.plan_type) {
                    return product;
                }
            })[0];

            this.canSwitchPlan = parseInt(value) !== parseInt(product.id);

            if (!this.canSwitchPlan) {
                if (this.submitConfig.styleClass.indexOf('disabled') < 0) {
                    this.submitConfig.styleClass.push('disabled');
                }
            } else {
                if (this.submitConfig.styleClass.indexOf('disabled') > -1) {
                    this.submitConfig.styleClass.splice(
                        this.submitConfig.styleClass.indexOf('disabled'),
                        1,
                    );
                }
            }
        },
    },
    mounted() {
        this.applyDefaultSelectedProduct(this.products);
        AccountService.setHttpService(this.$http);

        this.enhancedPaymentSummaryEnabled =
            this.$store.state.serverData.enhancedPaymentSummaryEnabled;
    },
    methods: {
        ...mapMutations('ftc', ['setTransactionProcessing']),
        applyDefaultSelectedProduct(products) {
            /** TODO - this.products is never set, which means this code is never run
             * https://paramount.atlassian.net/browse/PPIWEBM-1520
             **/
            if (products && this.selectedID === null) {
                let selectedProduct = {
                    id: null,
                };
                if (this.userProduct.plan_type === products[0].plan_type) {
                    selectedProduct = products.find(
                        (item) => item.plan_type !== this.userProduct.plan_type,
                    );
                } else {
                    selectedProduct = products.find((item) => item.tier === this.userProduct.tier);
                }

                this.selectedID = selectedProduct.id;
            }
        },
        enableBtnSubmit() {
            this.isProcessing = false;
            this.setTransactionProcessing(false);
        },
        disableBtnSubmit() {
            this.isProcessing = true;
            this.setTransactionProcessing(true);
        },
        cancelOnClicked() {
            this.disableBtnSubmit();
            this.$trackingService.trackAction(this.trackActionName.SWITCH_PLAN_CANCEL);
            this.redirectToAccount();
        },
        submitOnClicked(displayText = '') {
            let url = `${AA_XHR_BASE_PATH}switch/process/`;
            let httpData = this.buildHttpData();
            const {
                selectedProduct,
                isUpgradingToCF,
                notificationPageAttributeKey,
                isCrossGrade,
                trackActionType,
                purchasePaymentMethod,
            } = this.prepDataForSubmission();

            if (selectedProduct) {
                let params = {
                    plan: selectedProduct.code,
                    direction: selectedProduct?.switchDirection || '',
                };

                const appliedPromo = this?.coupon?.recurlyInfo?.code ?? null;
                // we do not allow free trial extensions during switch plan
                const isValidPromo =
                    this.coupon?.recurlyInfo?.discount?.type !== COUPON_TYPES.FREE_TRIAL &&
                    appliedPromo === this.promo &&
                    appliedPromo !== this?.currentSubscriptionPromo;

                if (this.promo && isValidPromo) {
                    params.couponCode = this.promo;
                }
                httpData = this.buildHttpData(params);

                // if user hasn't seen prompt modal and confirmed yet, show the prompt modal
                if (
                    !this.enhancedPaymentSummaryEnabled &&
                    !this.confirmSwitchPlan &&
                    this?.switchPlanPrompt
                ) {
                    this.switchPlanPrompt();
                    return;
                }

                this.disableBtnSubmit();

                const submitTransaction = () => {
                    this.$http.doPost(url, httpData).then((res) => {
                        if (res.success) {
                            this.enableBtnSubmit();
                            // TO BE REIMPLEMENTED
                            if (isCrossGrade) {
                                this.trackCrossgradeSuccess(selectedProduct, displayText);
                            } else {
                                let billingInfo =
                                    this.$store.getters.getServerDataByKey('billingInfo');
                                const args = {
                                    pageType: isUpgradingToCF
                                        ? pageType.UPGRADE
                                        : pageType.DOWNGRADE,
                                    ctaText: displayText,
                                    productCurrentSku: this.getCurrentPlan.recurlyCode,
                                    productNewSku: selectedProduct?.recurlyCode,
                                    purchasePaymentMethod,
                                    purchaseProductName: selectedProduct?.titleForTracking,
                                    productOfferPeriod: selectedProduct?.trialString,
                                };
                                if (isUpgradingToCF) {
                                    args.upgradeConfirm = 1;
                                } else {
                                    args.downgradeConfirm = 1;
                                }

                                this.trackSwitchPlan(selectedProduct, trackActionType, args);
                            }

                            if (this.promo) {
                                this.removePromoCookieAfterApply();
                            }
                            if (isUpgradingToCF) {
                                this.hideAllAccessUpsellNavItem();
                            }

                            localStorage.setItem('pickedTier', '');

                            this.redirectToAccount({
                                notification: 'switch-plan-success',
                                messageKey: notificationPageAttributeKey,
                            });
                        } else {
                            if (res.message == MAX_DISCOUNT_ALREADY_APPLIED) {
                                this.$router.push({
                                    name: 'GenericError',
                                    params: {
                                        heading: 'oops_something_went_wrong',
                                        subHeading: ['direct_link_general_error', 'dl_error_100'],
                                        errorTrackingPageType: 'switchplan_directLink_error',
                                    },
                                });
                                return;
                            }

                            // TO BE REIMPLEMENTED
                            // this.trackCrossgradeFailure(res.message);

                            this.openNotification(
                                new NotificationBarModel({
                                    success: false,
                                    message: getLocale(
                                        res.data?.errorCode ?? 'oh_no_something_went_wrong',
                                    ),
                                }),
                            );

                            this.enableBtnSubmit();
                        }
                    });
                };
                submitTransaction();
            }
        },

        isUserUpgradingPlan() {
            const currentUserPlanTier = this.userProduct?.tier;
            const currentUserPlanType = this.userProduct?.plan_type;

            const newSelectedUserPlanTier = this.getSelectedPlan?.tier;
            const newSelectedUserPlanType = this.getSelectedPlan?.planType;

            const isUpgradingUserPlanTier = newSelectedUserPlanTier > currentUserPlanTier;
            const isUpgradingUserPlanType =
                currentUserPlanType === 'monthly' && newSelectedUserPlanType === 'annual';

            return isUpgradingUserPlanTier || isUpgradingUserPlanType;
        },

        getPurchasePaymentMethod() {
            const CREDIT_CARD = 'credit_card';
            const PAYPAL = 'paypal';
            const GIFT_CARD = 'gift_card';
            const RECURLY_BALANCE = 'recurly_balance';
            let currentPaymentMethod = null;
            if (this.getBillingInfo) {
                const billingInfo = this.getBillingInfo;
                if (this.hasValidStoredPaymentMethod) {
                    if (billingInfo.isTypeCard) {
                        currentPaymentMethod = CREDIT_CARD;
                    } else if (billingInfo.isTypePaypal) {
                        currentPaymentMethod = PAYPAL;
                    } else if (billingInfo.isTypeGiftCard) {
                        currentPaymentMethod = GIFT_CARD;
                    } else {
                        currentPaymentMethod = RECURLY_BALANCE;
                    }
                }
            }

            return currentPaymentMethod;
        },

        prepDataForSubmission() {
            let notificationPageAttributeKey = '';
            let isUpgradingToCF = false;
            let isSwitchingToAnnual = false;
            const userPlan = this.userProduct || this.getCurrentPlan;
            const selectedProduct = this.getSelectedPlan;
            const purchasePaymentMethod = this.getPurchasePaymentMethod();

            let isCrossGrade = false;
            let trackActionType = '';

            if (selectedProduct) {
                if (
                    userPlan.product_tier === selectedProduct.tier ||
                    userPlan.tier === selectedProduct.tier
                ) {
                    isCrossGrade = true;
                } else if (selectedProduct.switchDirection === 'downgrade') {
                    notificationPageAttributeKey = 'switch_success_downgrade';
                    trackActionType = this.trackActionName.SWITCH_PLAN_DOWNGRADE_COMPLETE;
                } else if (selectedProduct.switchDirection === 'upgrade') {
                    notificationPageAttributeKey = 'switch_success_upgrade';
                    isUpgradingToCF = true;
                    trackActionType = this.trackActionName.SWITCH_PLAN_UPGRADE_COMPLETE;
                }

                if (!selectedProduct.switchDirection) {
                    if (userPlan.tier > selectedProduct.tier) {
                        notificationPageAttributeKey = 'switch_success_downgrade';
                        trackActionType = this.trackActionName.SWITCH_PLAN_DOWNGRADE_COMPLETE;
                    } else if (userPlan.tier < selectedProduct.tier) {
                        notificationPageAttributeKey = 'switch_success_upgrade';
                        isUpgradingToCF = true;
                        trackActionType = this.trackActionName.SWITCH_PLAN_UPGRADE_COMPLETE;
                    }
                }

                // user is trying to switch from monthly plan to annual plan (vice versa is not allowed yet)
                if (
                    userPlan.plan_type === Product.TYPE_MONTHLY &&
                    selectedProduct.planType === Product.TYPE_ANNUAL
                ) {
                    notificationPageAttributeKey = 'switch_success_upgrade_annual';

                    isSwitchingToAnnual = true;
                }
            }

            return {
                selectedProduct,
                isUpgradingToCF,
                isSwitchingToAnnual,
                notificationPageAttributeKey,
                isCrossGrade,
                trackActionType,
                purchasePaymentMethod,
            };
        },
        hideAllAccessUpsellNavItem() {
            let aaupsellnav = document.querySelector(
                '#global-header-container li.all-access-upsell-button',
            );

            if (aaupsellnav) {
                aaupsellnav.style.display = 'none';
            }
        },
        isValidPromoToSubmit() {
            return this.isValidPromoList[this.selectedID];
        },
        promoSuccess() {
            this.removePromoCookieAfterApply();
        },
        applyPromo(callback = () => {}) {
            if (this.isValidPromoToSubmit()) {
                this.$trackingService.trackAction(this.trackActionName.SWITCH_PLAN_UPGRADE_OFFER);

                let httpData = {
                    tk_trp: this.authToken,
                    coupon: this.promo,
                };

                AccountService.redeemCoupon(httpData)
                    .then((res) => {
                        if (!res.success) {
                            this.openNotification(
                                new NotificationBarModel({
                                    success: false,
                                    message: res.message,
                                }),
                            );
                        }
                        this.removePromoCookieAfterApply();
                        callback();
                    })
                    .catch((err) => {
                        this.openNotification(
                            new NotificationBarModel({
                                success: false,
                                message: err.message,
                            }),
                        );
                        callback();
                    });
            } else {
                callback();
            }
        },
        applyPromoAfterUpgrade() {
            this.applyPromo(this.redirectToAccount);
        },

        removePromoCookieAfterApply() {
            AccountService.removePromoCookie()
                .then((res) => {
                    // nothing to follow up with, cookie was removed
                })
                .catch((err) => {
                    // swallow error
                });
        },
        redirectToAccount(notificationQuery = null) {
            let queryParams = {
                name: 'AccountMain',
                params: { loadServerData: true, updateCurrentPlan: true },
            };
            if (notificationQuery) {
                queryParams.query = notificationQuery;
            }
            this.$router.push(queryParams);
        },
        handleValidPromo(obj) {
            this.isValidPromoList[obj.productId] = obj.isValid;
        },
        trackSwitchPlan(selectedProduct, action, args = {}) {
            const trackingParams = {
                ...args,
                productPricingPlan: selectedProduct.planType,
                productCurrentSku: this.getCurrentPlan.product_code,
                pickPlanType: selectedProduct.planTier,
                pickPlanSku: selectedProduct.recurlyCode,
                purchaseProduct: selectedProduct.recurlyCode,
                purchaseProductName: selectedProduct.titleForTracking,
            };

            if (this.ftcEnabled) {
                trackingParams.ftcCheckboxShown = this.showCheckbox ? '1' : '0';
                trackingParams.ftcCheckboxSelect =
                    this.getCoolingRightsWaived && this.showCheckbox ? '1' : '0';
            }

            this.$trackingService.trackAction(action, trackingParams);
        },
        trackCrossgradeSuccess(selectedProduct, ctaText) {
            let billingInfo = this.$store.getters.getServerDataByKey('billingInfo');
            const trackingParams = {
                pageType: pageType.CROSSGRADE,
                ctaText,
                productCurrentSku: this.getCurrentPlan.product_code,
                productNewSku: selectedProduct.recurlyCode,
                userRegId: this.user.regID,
                userStatus: this.user.svod.package_status,
                userType: this.user.svod.status,
                pickPlanType: selectedProduct.planTier,
                pickPlanSku: selectedProduct.recurlyCode,
                purchaseProduct: selectedProduct.recurlyCode,
                purchaseProductName: selectedProduct.titleForTracking,
                purchasePrice: selectedProduct.price,
                productPricingPlan: selectedProduct.planType,
                purchasePaymentMethod: billingInfo.paymentMethod,
                // users forfeit trial when switching plans
                productOfferPeriod: selectedProduct.trialString ?? '',
                crossgradeConfirm: 1,
            };

            if (this.ftcEnabled) {
                trackingParams.ftcCheckboxShown = this.showCheckbox ? '1' : '0';
                trackingParams.ftcCheckboxSelect =
                    this.getCoolingRightsWaived && this.showCheckbox ? '1' : '0';
            }

            // Add coupon code if present
            if (this.promo && this.isValidPromoToSubmit()) {
                trackingParams.purchasePromoCode = this.promo;
            }

            this.$trackingService.trackAction(
                this.trackActionName.CROSSGRADE_COMPLETE,
                trackingParams,
            );
        },
        trackCrossgradeFailure(error) {
            this.$trackingService.trackLog(logType.SWITCH_PLAN_ERROR, error);
        },
    },
};
